import React, { FC, useEffect, useState } from 'react'
import { useFetch } from 'use-http';
import { api, makeHeaders } from '../../util/api';
import { Button, Card, Checkbox, Divider, Grid, HStack, Input, Stack, Text } from '@chakra-ui/react';
import HeadingSection from '../../components/HeadingSection';
import Loading from '../../components/Misc/Loading';
import { FiFilePlus } from 'react-icons/fi';
import TextLabel from '../../components/Misc/LText';
import { ERROR_MESSAGE, useNotify } from '../../hooks/useNotify';

interface SurveySettings {
  id: number;
  name: boolean;
  lastName: boolean;
  email: boolean;
  phone: boolean;
  postalCode: boolean;
  age: boolean;
  gender: boolean;
  curp: boolean;
  cic: boolean;
  section: boolean;
  validate: boolean;
  goal: number;
  emailNotifications: boolean;
  emailAddress: string;
}

const SettingsPage: FC = () => {
  const notify = useNotify()
  const [id, setId] = useState<null | number>(null);
  const [name, setName] = useState<boolean>(false);
  const [lastName, setLastName] = useState<boolean>(false);
  const [email, setEmail] = useState<boolean>(false);
  const [phone, setPhone] = useState<boolean>(false);
  const [postalCode, setPostalCode] = useState<boolean>(false);
  const [age, setAge] = useState<boolean>(false);
  const [curp, setCurp] = useState<boolean>(false);
  const [cic, setCic] = useState<boolean>(true);
  const [gender, setGender] = useState<boolean>(false);
  const [section, setSection] = useState<boolean>(false);
  const [validate, setValidate] = useState<boolean>(false);
  const [goal, setGoal] = useState<number>(100);
  const [emailNotifications, setEmailNotifications] = useState<boolean>(false);
  const [emailAddress, setEmailAddress] = useState<string>('');

  const {
    get,
    post,
    patch,
    cache,
    loading,
    response,
    error,
  } = useFetch(`${api}/admin/config`, makeHeaders());

  const refreshSettings = async () => {
    cache.clear();
    const updatedSettings: SurveySettings = await get();
    if (!error && response.ok && updatedSettings) {
      setId(updatedSettings.id);
      setName(updatedSettings.name);
      setLastName(updatedSettings.lastName);
      setEmail(updatedSettings.email);
      setPhone(updatedSettings.phone);
      setPostalCode(updatedSettings.postalCode);
      setAge(updatedSettings.age);
      setCurp(updatedSettings.curp);
      setGender(updatedSettings.gender);
      setCic(updatedSettings.cic);
      setSection(updatedSettings.section);
      setValidate(updatedSettings.validate);
      setGoal(updatedSettings.goal);
      setEmailNotifications(updatedSettings.emailNotifications);
      setEmailAddress(updatedSettings.emailAddress);
    } else {
      notify(ERROR_MESSAGE, 'error');
    }
  };

  const createSettings = async () => {
    cache.clear();
    const newSettings: SurveySettings = await post();
    if (!error && response.ok && newSettings) {
      notify('Configuración creada', 'success');
      setId(newSettings.id);
      setName(newSettings.name);
      setLastName(newSettings.lastName);
      setEmail(newSettings.email);
      setPhone(newSettings.phone);
      setPostalCode(newSettings.postalCode);
      setAge(newSettings.age);
      setCurp(newSettings.curp);
      setGender(newSettings.gender);
      setCic(newSettings.cic);
      setSection(newSettings.section);
      setValidate(newSettings.validate);
      setGoal(newSettings.goal);
      setEmailNotifications(newSettings.emailNotifications);
      setEmailAddress(newSettings.emailAddress);
    } else {
      notify(ERROR_MESSAGE, 'error');
    }
  };

  const saveSettings = async () => {
    cache.clear();
    if (!id) return;
    const updatedSettings: SurveySettings = await patch(`/${id}`, {
      name,
      lastName,
      email,
      phone,
      postalCode,
      age,
      curp,
      cic,
      section,
      gender,
      validate,
      goal,
      emailNotifications,
      emailAddress,
    });
    if (!error && response.ok && updatedSettings) {
      notify('Configuración actualizada', 'success');
      setId(updatedSettings.id);
      setName(updatedSettings.name);
      setLastName(updatedSettings.lastName);
      setEmail(updatedSettings.email);
      setPhone(updatedSettings.phone);
      setPostalCode(updatedSettings.postalCode);
      setAge(updatedSettings.age);
      setCurp(updatedSettings.curp);
      setGender(updatedSettings.gender);
      setCic(updatedSettings.cic);
      setSection(updatedSettings.section);
      setValidate(updatedSettings.validate);
      setGoal(updatedSettings.goal);
      setEmailNotifications(updatedSettings.emailNotifications);
      setEmailAddress(updatedSettings.emailAddress);
    } else {
      notify(ERROR_MESSAGE, 'error');
    }
  };

  const updateGoalHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!value) {
      setGoal(0);
    }

    if (isNaN(value)) {
      return;
    }
    setGoal(value);
  };

  useEffect(() => {
    refreshSettings(); // eslint-disable-next-line
  }, []);

  return (
    <>
      <Stack maxW='container.sm'>
        <Card p={4} mb={2} variant='outline'>
          <HeadingSection title="Configuración de las encuestas" />
          <Loading isLoading={loading} />
          {!id && !loading && error && (
            <Text fontSize='sm' fontWeight='medium' color='gray.500' textAlign='center'>
              Ha ocurrido un error al cargar la configuración.
            </Text>
          )}
          {!id && !loading && !error && response.ok && (
            <>
              <Text fontSize='sm' fontWeight='medium' color='gray.500' textAlign='center'>
                No se ha creado ninguna configuración.
              </Text>

              <HStack justify='center' mt={2}>
                <Button
                  size='sm'
                  colorScheme='brand'
                  onClick={createSettings}
                  leftIcon={<FiFilePlus />}
                >
                  Crear configuración
                </Button>
              </HStack>
            </>
          )}

          {!loading && id && (
            <>
              <Grid templateColumns='4fr 2fr' gap={4} mt={4}>
                <TextLabel title="Meta de respuestas por encuesta" />
                <Input
                  type='text'
                  value={goal}
                  onChange={updateGoalHandler}
                  size={'sm'}
                  min={0}
                  max={999_999_999}
                  maxLength={9}
                />
                <TextLabel
                  title="Validar respuestas"
                  info='Mostrar sólo respuestas validadas en las estadísticas.'
                />
                <Checkbox isChecked={validate} onChange={(e) => setValidate(e.target.checked)} />
              </Grid>
              <Divider mt={4} mb={2} />
              <HeadingSection title="Datos a solicitar" />
              <Grid templateColumns='4fr 2fr' gap={4} mt={4}>
                <TextLabel title='CIC (Identificador INE)' />
                <Checkbox
                  isChecked={cic}
                  isDisabled
                  onChange={(e) => setCic(e.target.checked)}
                />
                <TextLabel title='Sección electoral' />
                <Checkbox isChecked={section} onChange={(e) => setSection(e.target.checked)} />
                <TextLabel title='Nombre' />
                <Checkbox isChecked={name} onChange={(e) => setName(e.target.checked)} />
                <TextLabel title='Apellido' />
                <Checkbox isChecked={lastName} onChange={(e) => setLastName(e.target.checked)} />
                <TextLabel title='Género' />
                <Checkbox isChecked={gender} onChange={(e) => setGender(e.target.checked)} />
                <TextLabel title='Correo electrónico' />
                <Checkbox isChecked={email} onChange={(e) => setEmail(e.target.checked)} />
                <TextLabel title='Edad' />
                <Checkbox isChecked={age} onChange={(e) => setAge(e.target.checked)} />
                <TextLabel title='Teléfono' />
                <Checkbox isChecked={phone} onChange={(e) => setPhone(e.target.checked)} />
                <TextLabel title='Código postal' />
                <Checkbox isChecked={postalCode} onChange={(e) => setPostalCode(e.target.checked)} />
                <TextLabel title='CURP' />
                <Checkbox isChecked={curp} onChange={(e) => setCurp(e.target.checked)} />
              </Grid>
              <Divider mt={4} mb={2} />
              <HeadingSection title="Notificaciones" />

              <Grid templateColumns='4fr 2fr' gap={4} mt={4}>
                <TextLabel
                  title="Recibir notificaciones por correo electrónico"
                  info='Notificar cuando se responda una encuesta.'
                />
                <Checkbox
                  isChecked={emailNotifications}
                  onChange={() => setEmailNotifications(!emailNotifications)}
                />
                <TextLabel title="Dirección de correo electrónico" />
                <Input
                  size={'sm'}
                  type='email'
                  value={emailAddress || ''}
                  placeholder='Correo electrónico'
                  onChange={(e) => setEmailAddress(e.target.value)}
                  isDisabled={!emailNotifications}
                />
              </Grid>
              <Divider mt={4} mb={2} />
              <HStack justify='end'>
                <Button
                  size='sm'
                  colorScheme='brand'
                  onClick={saveSettings}
                  isLoading={loading}
                >
                  Guardar
                </Button>
              </HStack>
            </>
          )}
        </Card>
      </Stack>
    </>
  );
}

export default SettingsPage;
